import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Glasses, CheckCircle2 } from 'lucide-react';
import { VideoData } from './types';
import { useTranslation } from 'react-i18next';

interface VideoContentProps {
  video: VideoData;
  onEnded?: () => void;
  onManualComplete?: () => void;
  isCompleted?: boolean;
}

export function VideoContent({ video, onEnded, onManualComplete, isCompleted }: VideoContentProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!video.video_url) {
    return (
      <div className="flex items-center justify-center h-48 bg-muted rounded-lg">
        <p className="text-muted-foreground">{t('common:activityViewer.noVideo', 'No video available')}</p>
      </div>
    );
  }

  if (video.is_vr) {
    return (
      <div className="space-y-4">
        <div className="relative w-full aspect-video bg-muted rounded-lg overflow-hidden group">
          <video
            className="w-full h-full object-cover"
            src={video.video_url}
            preload="none"
            muted
          />
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
            <Glasses className="w-16 h-16 text-white opacity-80" />
          </div>
        </div>
        <Button
          className="w-full"
          size="lg"
          onClick={() => {
            const params = new URLSearchParams({
              url: video.video_url!,
              title: video.title,
              projection: video.vr_projection || 'equirectangular',
              stereo: video.vr_stereo_mode || 'mono'
            });
            navigate(`/vr-player?${params.toString()}`);
          }}
        >
          <Glasses className="w-5 h-5 mr-2" />
          {t('common:activityViewer.watchVR', 'Watch in VR')}
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={onManualComplete}
          disabled={isCompleted}
        >
          <CheckCircle2 className="w-4 h-4 mr-2" />
          {isCompleted 
            ? t('common:activityViewer.completedBadge', 'Completed')
            : t('common:activityViewer.markComplete', 'Mark as Completed')
          }
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <video 
        controls 
        className="w-full rounded-lg"
        src={video.video_url}
        autoPlay
        onEnded={onEnded}
      >
        {t('common:activityViewer.unsupportedVideo', 'Your browser does not support the video tag.')}
      </video>
      <Button
        variant="outline"
        className="w-full"
        onClick={onManualComplete}
        disabled={isCompleted}
      >
        <CheckCircle2 className="w-4 h-4 mr-2" />
        {isCompleted 
          ? t('common:activityViewer.completedBadge', 'Completed')
          : t('common:activityViewer.markComplete', 'Mark as Completed')
        }
      </Button>
    </div>
  );
}
